import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import LiveStatusStrip from '@/components/docs/LiveStatusStrip';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import {
  Copy,
  ArrowRight,
  Clock,
  KeyRound,
  Fingerprint,
  ShieldCheck,
  Globe,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '@/config/api';
import RequestPartnershipModal from '@/components/landing/RequestPartnershipModal';
import { useSEO } from '@/lib/seo';
import { Helmet } from 'react-helmet-async';

type CodeLanguage = 'curl' | 'javascript' | 'python';

interface FieldRow {
  field: string;
  type: string;
  required?: boolean;
  description: string;
}

interface EndpointDef {
  id: string;
  method: 'GET' | 'POST';
  path: string;
  description: string;
  headers: string[];
  request?: FieldRow[];
  query?: FieldRow[];
  response: FieldRow[];
  note?: string;
  snippets: Record<CodeLanguage, (key: string) => string>;
}

const IntegrationHub = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<CodeLanguage>('curl');
  const [partnershipModalOpen, setPartnershipModalOpen] = useState(false);

  // Public docs use a clear placeholder. Authenticated partners get
  // personalized snippets inside the Hub at /hub/developers.
  const displayKey = 'YOUR_API_KEY';

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied!', description: `${label} copied to clipboard` });
  };

  const seo = useSEO(
    'API Reference',
    "Complete API reference for ClearSlot's coordination infrastructure — endpoints, authentication, score model and error codes.",
    'https://clearslot.io/docs/integrate',
  );

  // ---------------- Endpoint definitions ----------------

  const bookingCreateBody = {
    platform_id: 'YOUR_PLATFORM_ID',
    customer_hash: '+4520123456',
    restaurant_hash: 'rest_456',
    reservation_time: '2026-06-01T19:00:00Z',
    duration_minutes: 120,
    identity_scope: 'local',
  };

  const writeBodyOnly = (k: string, body: Record<string, unknown>) => ({
    curl: `curl -X POST "${API_BASE_URL}${k}" \\
  -H "x-api-key: ${displayKey}" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -d '${JSON.stringify(body, null, 2)}'`,
    javascript: `const res = await fetch("${API_BASE_URL}${k}", {
  method: "POST",
  headers: {
    "x-api-key": "${displayKey}",
    "Content-Type": "application/json",
    "Idempotency-Key": crypto.randomUUID()
  },
  body: JSON.stringify(${JSON.stringify(body, null, 2)})
});
const data = await res.json();`,
    python: `import requests, uuid

res = requests.post(
    "${API_BASE_URL}${k}",
    headers={
        "x-api-key": "${displayKey}",
        "Content-Type": "application/json",
        "Idempotency-Key": str(uuid.uuid4())
    },
    json=${JSON.stringify(body, null, 4).replace(/"/g, '"')}
)
print(res.json())`,
  });

  const getSnippet = (path: string, query?: Record<string, string>) => {
    const qs = query ? '?' + new URLSearchParams(query).toString() : '';
    return {
      curl: `curl "${API_BASE_URL}${path}${qs}" \\
  -H "x-api-key: ${displayKey}"`,
      javascript: `const res = await fetch("${API_BASE_URL}${path}${qs}", {
  headers: { "x-api-key": "${displayKey}" }
});
const data = await res.json();`,
      python: `import requests

res = requests.get(
    "${API_BASE_URL}${path}${qs}",
    headers={"x-api-key": "${displayKey}"}
)
print(res.json())`,
    };
  };

  const endpoints: EndpointDef[] = [
    {
      id: 'booking-create',
      method: 'POST',
      path: '/v1/booking/create',
      description: 'Register a new booking signal. Returns a coordination advisory.',
      headers: ['x-api-key', 'Idempotency-Key', 'Content-Type: application/json'],
      request: [
        { field: 'platform_id', type: 'string', required: true, description: 'Your platform identifier.' },
        { field: 'customer_hash', type: 'string', required: true, description: 'Raw customer identifier (hashed server-side).' },
        { field: 'restaurant_hash', type: 'string', required: true, description: 'Venue/location identifier.' },
        { field: 'reservation_time', type: 'ISO 8601', required: true, description: 'Booking start time, e.g. 2026-06-01T19:00:00Z.' },
        { field: 'duration_minutes', type: 'number', required: false, description: 'Duration 15–480 min. Default: 120.' },
        { field: 'identity_scope', type: 'string', required: false, description: '"local" or "global". Default: "local".' },
      ],
      response: [
        { field: 'ok', type: 'boolean', description: 'true on success.' },
        { field: 'signal', type: 'string', description: '"BOOKING_CONFIRMED".' },
        { field: 'booking_id', type: 'string', description: 'Format "bk_" + 16 hex (e.g. "bk_7f3a9c8b2e1df3a9"). Store immediately — required for all subsequent cancel, no-show, and completed calls on this booking.' },
        { field: 'message', type: 'string', description: 'Human-readable confirmation.' },
        { field: 'version', type: 'string', description: 'API version.' },
      ],
      note: 'Store booking_id immediately — required for all subsequent cancel, no-show, and completed calls on this booking.',
      snippets: {
        curl: () => writeBodyOnly('/v1/booking/create', bookingCreateBody).curl,
        javascript: () => writeBodyOnly('/v1/booking/create', bookingCreateBody).javascript,
        python: () => writeBodyOnly('/v1/booking/create', bookingCreateBody).python,
      },
    },
    {
      id: 'booking-cancel',
      method: 'POST',
      path: '/v1/booking/cancel',
      description: 'Cancel a booking. Applies a late-cancel score delta if within penalty window.',
      headers: ['x-api-key', 'Idempotency-Key', 'Content-Type: application/json'],
      request: [
        { field: 'platform_id', type: 'string', required: true, description: 'Your platform identifier.' },
        { field: 'customer_hash', type: 'string', required: true, description: 'Raw customer identifier.' },
        { field: 'restaurant_hash', type: 'string', required: true, description: 'Venue identifier.' },
        { field: 'identity_scope', type: 'string', required: false, description: 'Default: "local".' },
      ],
      response: [
        { field: 'ok', type: 'boolean', description: 'true on success.' },
        { field: 'signal', type: 'string', description: '"BOOKING_CANCELLED".' },
        { field: 'version', type: 'string', description: 'API version.' },
      ],
      note: 'A score delta of -15 is applied with a 30-day recovery window.',
      snippets: {
        curl: () => writeBodyOnly('/v1/booking/cancel', { platform_id: 'YOUR_PLATFORM_ID', customer_hash: '+4520123456', restaurant_hash: 'rest_456', identity_scope: 'local' }).curl,
        javascript: () => writeBodyOnly('/v1/booking/cancel', { platform_id: 'YOUR_PLATFORM_ID', customer_hash: '+4520123456', restaurant_hash: 'rest_456', identity_scope: 'local' }).javascript,
        python: () => writeBodyOnly('/v1/booking/cancel', { platform_id: 'YOUR_PLATFORM_ID', customer_hash: '+4520123456', restaurant_hash: 'rest_456', identity_scope: 'local' }).python,
      },
    },
    {
      id: 'event-no-show',
      method: 'POST',
      path: '/v1/event/no-show',
      description: 'Report a no-show event for a customer.',
      headers: ['x-api-key', 'Idempotency-Key', 'Content-Type: application/json'],
      request: [
        { field: 'platform_id', type: 'string', required: true, description: 'Your platform identifier.' },
        { field: 'customer_hash', type: 'string', required: true, description: 'Raw customer identifier.' },
        { field: 'restaurant_hash', type: 'string', required: true, description: 'Venue identifier.' },
        { field: 'identity_scope', type: 'string', required: false, description: 'Default: "local".' },
      ],
      response: [
        { field: 'ok', type: 'boolean', description: 'true on success.' },
        { field: 'signal', type: 'string', description: '"NO_SHOW_RECORDED".' },
        { field: 'version', type: 'string', description: 'API version.' },
      ],
      note: 'Score delta -30, TTL 365 days.',
      snippets: {
        curl: () => writeBodyOnly('/v1/event/no-show', { platform_id: 'YOUR_PLATFORM_ID', customer_hash: '+4520123456', restaurant_hash: 'rest_456', identity_scope: 'local' }).curl,
        javascript: () => writeBodyOnly('/v1/event/no-show', { platform_id: 'YOUR_PLATFORM_ID', customer_hash: '+4520123456', restaurant_hash: 'rest_456', identity_scope: 'local' }).javascript,
        python: () => writeBodyOnly('/v1/event/no-show', { platform_id: 'YOUR_PLATFORM_ID', customer_hash: '+4520123456', restaurant_hash: 'rest_456', identity_scope: 'local' }).python,
      },
    },
    {
      id: 'event-completed',
      method: 'POST',
      path: '/v1/event/completed',
      description: 'Confirm a booking was completed successfully. Improves guest score.',
      headers: ['x-api-key', 'Idempotency-Key', 'Content-Type: application/json'],
      request: [
        { field: 'platform_id', type: 'string', required: true, description: 'Your platform identifier.' },
        { field: 'customer_hash', type: 'string', required: true, description: 'Raw customer identifier.' },
        { field: 'restaurant_hash', type: 'string', required: true, description: 'Venue identifier.' },
        { field: 'identity_scope', type: 'string', required: false, description: 'Default: "local".' },
      ],
      response: [
        { field: 'ok', type: 'boolean', description: 'true on success.' },
        { field: 'signal', type: 'string', description: '"BOOKING_COMPLETED".' },
        { field: 'version', type: 'string', description: 'API version.' },
      ],
      note: 'Score delta +10, TTL 180 days.',
      snippets: {
        curl: () => writeBodyOnly('/v1/event/completed', { platform_id: 'YOUR_PLATFORM_ID', customer_hash: '+4520123456', restaurant_hash: 'rest_456', identity_scope: 'local' }).curl,
        javascript: () => writeBodyOnly('/v1/event/completed', { platform_id: 'YOUR_PLATFORM_ID', customer_hash: '+4520123456', restaurant_hash: 'rest_456', identity_scope: 'local' }).javascript,
        python: () => writeBodyOnly('/v1/event/completed', { platform_id: 'YOUR_PLATFORM_ID', customer_hash: '+4520123456', restaurant_hash: 'rest_456', identity_scope: 'local' }).python,
      },
    },
    {
      id: 'score',
      method: 'GET',
      path: '/v1/score/:customer_hash',
      description: 'Query the current coordination score for a guest.',
      headers: ['x-api-key'],
      query: [
        { field: 'platform_id', type: 'string', required: true, description: 'Your platform identifier.' },
        { field: 'identity_scope', type: 'string', required: false, description: 'Default: "local".' },
      ],
      response: [
        { field: 'score', type: 'number', description: '0–100. 75 = no prior signals (earned reputation baseline).' },
        { field: 'scale', type: 'string', description: '"0-100".' },
        { field: 'scope', type: 'string', description: 'The scope queried.' },
        { field: 'active_signals', type: 'number', description: 'Number of signals currently affecting score.' },
        { field: 'info', type: 'string', description: 'Optional contextual note.' },
        { field: 'version', type: 'string', description: 'API version.' },
      ],
      snippets: {
        curl: () => getSnippet('/v1/score/+4520123456', { platform_id: 'YOUR_PLATFORM_ID', identity_scope: 'local' }).curl,
        javascript: () => getSnippet('/v1/score/+4520123456', { platform_id: 'YOUR_PLATFORM_ID', identity_scope: 'local' }).javascript,
        python: () => getSnippet('/v1/score/+4520123456', { platform_id: 'YOUR_PLATFORM_ID', identity_scope: 'local' }).python,
      },
    },
    {
      id: 'billing-summary',
      method: 'GET',
      path: '/v1/billing/summary',
      description: 'Returns aggregate billing totals for your platform.',
      headers: ['x-api-key'],
      response: [
        { field: 'signal_checks', type: 'number', description: 'Count of score query events.' },
        { field: 'coordination_bookings', type: 'number', description: 'Count of coordination booking events.' },
        { field: 'total_revenue', type: 'number', description: 'Total billed in EUR.' },
        { field: 'version', type: 'string', description: 'API version.' },
      ],
      snippets: {
        curl: () => getSnippet('/v1/billing/summary').curl,
        javascript: () => getSnippet('/v1/billing/summary').javascript,
        python: () => getSnippet('/v1/billing/summary').python,
      },
    },
    {
      id: 'billing-ledger',
      method: 'GET',
      path: '/v1/billing/ledger',
      description: 'Returns paginated billing events for your platform.',
      headers: ['x-api-key'],
      query: [
        { field: 'limit', type: 'number', required: false, description: 'Max 200. Default: 50.' },
        { field: 'offset', type: 'number', required: false, description: 'Default: 0.' },
      ],
      response: [
        { field: 'data', type: 'array', description: '[{ event_type, restaurant_hash, amount_euro, created_at }]' },
        { field: 'version', type: 'string', description: 'API version.' },
      ],
      snippets: {
        curl: () => getSnippet('/v1/billing/ledger', { limit: '50', offset: '0' }).curl,
        javascript: () => getSnippet('/v1/billing/ledger', { limit: '50', offset: '0' }).javascript,
        python: () => getSnippet('/v1/billing/ledger', { limit: '50', offset: '0' }).python,
      },
    },
    {
      id: 'healthcheck',
      method: 'GET',
      path: '/v1/healthcheck',
      description: 'Returns API status. No authentication required.',
      headers: [],
      response: [
        { field: 'status', type: 'string', description: '"ready".' },
        { field: 'version', type: 'string', description: 'API version.' },
      ],
      snippets: {
        curl: () => `curl "${API_BASE_URL}/v1/healthcheck"`,
        javascript: () => `const res = await fetch("${API_BASE_URL}/v1/healthcheck");
const data = await res.json();`,
        python: () => `import requests
print(requests.get("${API_BASE_URL}/v1/healthcheck").json())`,
      },
    },
  ];

  const languages: { key: CodeLanguage; label: string }[] = [
    { key: 'curl', label: 'cURL' },
    { key: 'javascript', label: 'JavaScript' },
    { key: 'python', label: 'Python' },
  ];

  // ---------------- Reusable bits ----------------

  const LangTabs = () => (
    <div className="bg-muted p-1 rounded-lg inline-flex text-xs font-medium gap-1">
      {languages.map((lang) => (
        <button
          key={lang.key}
          onClick={() => setActiveTab(lang.key)}
          className={`px-3 py-1.5 rounded transition-all ${
            activeTab === lang.key
              ? 'bg-background shadow text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {lang.label}
        </button>
      ))}
    </div>
  );

  const CodeBlock = ({ code, label }: { code: string; label: string }) => (
    <div className="relative group">
      <pre className="bg-background rounded-lg p-4 border border-border overflow-x-auto text-xs font-mono text-foreground whitespace-pre">
        {code}
      </pre>
      <Button
        variant="outline"
        size="sm"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => copyToClipboard(code, label)}
      >
        <Copy className="h-3 w-3 mr-1" />
        Copy
      </Button>
    </div>
  );

  const FieldTable = ({ rows, title }: { rows: FieldRow[]; title: string }) => (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{title}</p>
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-foreground">Field</th>
              <th className="text-left px-3 py-2 font-medium text-foreground">Type</th>
              {rows.some((r) => r.required !== undefined) && (
                <th className="text-left px-3 py-2 font-medium text-foreground">Required</th>
              )}
              <th className="text-left px-3 py-2 font-medium text-foreground">Description</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.field} className={i !== rows.length - 1 ? 'border-t border-border' : 'border-t border-border'}>
                <td className="px-3 py-2">
                  <code className="font-mono text-xs text-primary bg-primary/10 px-1.5 py-0.5 rounded">{row.field}</code>
                </td>
                <td className="px-3 py-2 text-muted-foreground text-xs font-mono">{row.type}</td>
                {rows.some((r) => r.required !== undefined) && (
                  <td className="px-3 py-2 text-xs">
                    {row.required ? (
                      <Badge variant="outline" className="border-primary/30 text-primary">required</Badge>
                    ) : (
                      <span className="text-muted-foreground">optional</span>
                    )}
                  </td>
                )}
                <td className="px-3 py-2 text-muted-foreground text-xs">{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const errorCodes = [
    { http: 400, code: 'INVALID_REQUEST', when: 'Missing or malformed fields.' },
    { http: 400, code: 'INVALID_SCOPE', when: 'identity_scope not "local" or "global".' },
    { http: 400, code: 'IDEMPOTENCY_REQUIRED', when: 'Idempotency-Key header missing on write requests.' },
    { http: 400, code: 'INVALID_PLATFORM_ID', when: 'platform_id query parameter is missing on /v1/score.' },
    { http: 401, code: 'UNAUTHORIZED', when: 'Missing or invalid x-api-key.' },
    { http: 409, code: 'IDEMPOTENCY_CONFLICT', when: 'Same Idempotency-Key reused with different request body.' },
    { http: 415, code: 'INVALID_CONTENT_TYPE', when: 'POST request missing Content-Type: application/json.' },
    { http: 429, code: 'RATE_LIMIT_EXCEEDED', when: 'Exceeds 100 requests/minute — retry after backoff.' },
    { http: 500, code: 'SERVER_ERROR', when: 'Internal error — contact support.' },
    { http: 500, code: 'SERVER_MISCONFIGURED', when: 'Server environment is not correctly configured (contact support).' },
  ];

  return (
    <>
      {seo}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "Integrate ClearSlot API",
          "description": "Step-by-step guide to integrating ClearSlot's booking coordination API. Three endpoints, stateless design, live in one sprint.",
          "url": "https://clearslot.io/docs/integrate",
          "author": { "@type": "Organization", "name": "ClearSlot" },
          "publisher": { "@type": "Organization", "name": "ClearSlot", "url": "https://clearslot.io" }
        })}</script>
      </Helmet>
      <RequestPartnershipModal open={partnershipModalOpen} onOpenChange={setPartnershipModalOpen} />
      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="relative h-8 w-8">
                <svg viewBox="0 0 100 100" className="h-full w-full">
                  <defs>
                    <linearGradient id="logoGradIntegration" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="hsl(var(--primary))" />
                      <stop offset="100%" stopColor="hsl(var(--accent))" />
                    </linearGradient>
                  </defs>
                  <g transform="translate(10, 30) scale(0.36)">
                    <path
                      d="M40 55 C40 25 90 25 110 55 C130 85 180 85 180 55 C180 25 130 25 110 55 C90 85 40 85 40 55 Z"
                      fill="none"
                      stroke="url(#logoGradIntegration)"
                      strokeWidth="18"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </div>
              <span className="font-bold text-foreground text-lg">ClearSlot</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/hub">
                <Button variant="outline" size="sm">Partner Hub</Button>
              </Link>
              <Link to="/auth">
                <Button variant="default" size="sm">Sign In</Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="container mx-auto px-6 text-center relative"
          >
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
              <Clock className="h-3 w-3 mr-1" />
              API Reference · v1
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4">
              The ClearSlot <span className="text-primary">API</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Eight endpoints. One coordination layer. Built for booking platforms that need cross-platform guest signals without compromising neutrality.
            </p>

            {/* Live API status — real numbers from healthcheck logger */}
            <LiveStatusStrip />
          </motion.div>
        </section>

        {/* Section 2 — Authentication */}
        <section className="py-16 container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-primary/10">
                <KeyRound className="h-5 w-5 text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">Authentication</h2>
            </div>
            <p className="text-muted-foreground mb-6">
              All requests must include the <code className="bg-secondary px-1.5 py-0.5 rounded text-sm font-mono text-foreground">x-api-key</code> header.
              Keys are issued per partner platform. All traffic must use HTTPS. Live keys are prefixed{' '}
              <code className="bg-secondary px-1.5 py-0.5 rounded text-sm font-mono text-foreground">cs_live_</code> — sandbox keys{' '}
              <code className="bg-secondary px-1.5 py-0.5 rounded text-sm font-mono text-foreground">cs_test_</code>.
            </p>
            <CodeBlock
              code={`curl -H "x-api-key: ${displayKey}" ${API_BASE_URL}/v1/healthcheck`}
              label="Auth example"
            />
          </motion.div>
        </section>

        {/* Section 3 — Core concepts */}
        <section className="py-16 bg-card/50 border-y border-border">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="max-w-6xl mx-auto"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2 text-center">Core concepts</h2>
              <p className="text-muted-foreground text-center mb-10">Three primitives shape every request.</p>

              <div className="grid md:grid-cols-3 gap-6">
                <Card className="bg-card border-border">
                  <CardHeader>
                    <div className="p-2 rounded-lg bg-primary/10 w-fit mb-2">
                      <Globe className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg">Identity scope</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground space-y-2">
                    <p>Two values: <code className="font-mono text-foreground">local</code> and <code className="font-mono text-foreground">global</code>.</p>
                    <p><span className="text-foreground font-medium">local</span> — signals from your platform only.</p>
                    <p><span className="text-foreground font-medium">global</span> — aggregated across all ClearSlot partners.</p>
                    <p className="pt-2">Sent as <code className="font-mono text-foreground">identity_scope</code> on every request.</p>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <div className="p-2 rounded-lg bg-accent/10 w-fit mb-2">
                      <ShieldCheck className="h-5 w-5 text-accent" />
                    </div>
                    <CardTitle className="text-lg">Idempotency</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground space-y-2">
                    <p>All write endpoints require an <code className="font-mono text-foreground">Idempotency-Key</code> header. Use a UUID.</p>
                    <p>Keys are scoped per platform and expire after 24 hours.</p>
                    <p>Replaying the same key + body returns the cached response. Same key with a different body returns <code className="font-mono text-foreground">409 IDEMPOTENCY_CONFLICT</code>.</p>
                    <pre className="bg-background border border-border rounded p-2 text-xs font-mono text-foreground mt-2">Idempotency-Key: $(uuidgen)</pre>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <div className="p-2 rounded-lg bg-warning/10 w-fit mb-2">
                      <Fingerprint className="h-5 w-5 text-warning" />
                    </div>
                    <CardTitle className="text-lg">Customer hash</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground space-y-2">
                    <p>You send a raw customer identifier (e.g. phone number in E.164 format).</p>
                    <p>ClearSlot hashes it server-side using <span className="text-foreground">PBKDF2/SHA-256</span> with 100,000 iterations.</p>
                    <p>You never store the hashed form. <span className="text-foreground">No PII leaves your system.</span></p>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section 4 — Endpoint reference */}
        <section className="py-16 container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-5xl mx-auto"
          >
            <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Endpoint reference</h2>
                <p className="text-muted-foreground">All endpoints are versioned under <code className="font-mono text-foreground">/v1</code>.</p>
              </div>
              <LangTabs />
            </div>

            <Accordion type="multiple" className="space-y-3">
              {endpoints.map((ep) => (
                <AccordionItem
                  key={ep.id}
                  value={ep.id}
                  className="border border-border rounded-lg bg-card overflow-hidden data-[state=open]:border-primary/40"
                >
                  <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-secondary/30">
                    <div className="flex items-center gap-3 flex-1 text-left min-w-0">
                      <Badge
                        variant="outline"
                        className={`font-mono text-xs flex-shrink-0 ${
                          ep.method === 'GET'
                            ? 'border-success/40 text-success bg-success/10'
                            : 'border-primary/40 text-primary bg-primary/10'
                        }`}
                      >
                        {ep.method}
                      </Badge>
                      <code className="font-mono text-sm text-foreground truncate">{ep.path}</code>
                      <span className="text-xs text-muted-foreground hidden md:block truncate ml-2">{ep.description}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5">
                    <div className="space-y-5 pt-2">
                      <p className="text-sm text-muted-foreground md:hidden">{ep.description}</p>

                      {ep.headers.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                            Required headers
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {ep.headers.map((h) => (
                              <code key={h} className="text-xs font-mono bg-secondary px-2 py-1 rounded text-foreground">
                                {h}
                              </code>
                            ))}
                          </div>
                        </div>
                      )}

                      {ep.request && <FieldTable rows={ep.request} title="Request body" />}
                      {ep.query && <FieldTable rows={ep.query} title="Query parameters" />}
                      <FieldTable rows={ep.response} title="Response" />

                      {ep.note && (
                        <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground">
                          <span className="font-semibold text-primary">Note:</span> {ep.note}
                        </div>
                      )}

                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                          Example — {languages.find((l) => l.key === activeTab)?.label}
                        </p>
                        <CodeBlock code={ep.snippets[activeTab](displayKey)} label={`${ep.method} ${ep.path}`} />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </section>

        {/* Section 5 — Score model */}
        <section className="py-16 bg-card/50 border-y border-border">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2 text-center">How guest scores work</h2>
              <p className="text-muted-foreground text-center mb-8">Deterministic. No ML. No heuristics.</p>

              <Card className="bg-card border-border mb-6">
                <CardContent className="p-6">
                  <pre className="bg-background border border-border rounded-lg p-4 text-sm font-mono text-foreground text-center overflow-x-auto">
{`score = max(0, min(100, 75 + sum(active_deltas)))`}
                  </pre>
                </CardContent>
              </Card>

              <Card className="bg-card border-border overflow-hidden mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">Event</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">Delta</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">TTL</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-border">
                      <td className="px-4 py-3 text-foreground">No-show</td>
                      <td className="px-4 py-3 font-mono text-destructive">−30</td>
                      <td className="px-4 py-3 text-muted-foreground">365 days</td>
                    </tr>
                    <tr className="border-t border-border">
                      <td className="px-4 py-3 text-foreground">Late cancellation</td>
                      <td className="px-4 py-3 font-mono text-destructive">−15</td>
                      <td className="px-4 py-3 text-muted-foreground">30 days</td>
                    </tr>
                    <tr className="border-t border-border">
                      <td className="px-4 py-3 text-foreground">Completed booking</td>
                      <td className="px-4 py-3 font-mono text-success">+10</td>
                      <td className="px-4 py-3 text-muted-foreground">180 days</td>
                    </tr>
                  </tbody>
                </table>
              </Card>

              <ul className="text-sm text-muted-foreground space-y-2 list-disc pl-5">
                <li>Base score is <span className="text-foreground font-medium">75</span> when no prior signals exist — guests earn their way up to 100 through completed bookings.</li>
                <li>Signals expire automatically — score recovers over time.</li>
                <li>Exact weights beyond the above are not disclosed.</li>
                <li>Engine is deterministic. No ML, no heuristics.</li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Section 6 — Error codes */}
        <section className="py-16 container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2 text-center">Error codes</h2>
            <p className="text-muted-foreground text-center mb-8">All errors share the same envelope.</p>

            <Card className="bg-card border-border overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">HTTP</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">Code</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">When it occurs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorCodes.map((e) => (
                      <tr key={e.code} className="border-t border-border">
                        <td className="px-4 py-3 font-mono text-muted-foreground">{e.http}</td>
                        <td className="px-4 py-3">
                          <code className="font-mono text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">{e.code}</code>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{e.when}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <CodeBlock
              code={`{
  "error": {
    "code": "ERROR_CODE",
    "message": "Human-readable description"
  },
  "version": "v1"
}`}
              label="Error envelope"
            />
          </motion.div>
        </section>

        {/* Section 7 — CTA */}
        <section className="py-20 container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Ready to get started?</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Request partner access to receive your sandbox credentials, or try the public Sandbox Engine first.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="default" size="lg" className="gap-2" onClick={() => setPartnershipModalOpen(true)}>
                Request Partner Access
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Link to="/#sandbox-engine">
                <Button variant="outline" size="lg">Try the Sandbox Engine</Button>
              </Link>
            </div>
          </motion.div>
        </section>

        {/* Footer */}
        <footer className="border-t border-border py-8">
          <div className="container mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">© ClearSlot 2026. All rights reserved.</p>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
              <Link to="/hub" className="hover:text-foreground transition-colors">Partner Hub</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default IntegrationHub;
